import { SocialSharing, SocialSharingGroup } from '@change/ds-core-ui';
import type { Icon } from '@change/ds-icons';
import { EmailIcon, FacebookIcon, TwitterIcon, WhatsAppIcon } from '@change/ds-icons';

import type { I18nUtilities } from '@change-corgi/core-react/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';

import { useMovementShareActions } from './hooks/useMovementShareActions';

type Channel = 'facebook' | 'twitter' | 'email' | 'whatsapp';

const CHANNELS = new Set<string>(['facebook', 'twitter', 'email', 'whatsapp']);

const ICON_MAP: Record<Channel, React.FC<Icon>> = {
	facebook: FacebookIcon,
	twitter: TwitterIcon,
	email: EmailIcon,
	whatsapp: WhatsAppIcon,
};

const VARIANT_MAP: Record<Channel, 'facebook' | 'twitter' | 'default' | 'whatsapp'> = {
	facebook: 'facebook',
	twitter: 'twitter',
	email: 'default',
	whatsapp: 'whatsapp',
};

function getLabel(channel: Channel, translate: I18nUtilities['translate']): string {
	switch (channel) {
		case 'facebook':
			return translate('fe.components.psf.share.buttons.short.facebook');
		case 'twitter':
			return translate('fe.components.psf.share.buttons.short.twitter');
		case 'email':
			return translate('fe.components.psf.share.buttons.short.email');
		case 'whatsapp':
			return translate('fe.components.psf.share.buttons.short.whatsapp');
		default:
			return '';
	}
}

export const ShareGroup: React.FC = () => {
	const shareActions = useMovementShareActions();
	const { translate } = useI18n();

	return (
		<SocialSharingGroup layout="grid" className="mt-10 w-full grid-cols-2 items-start sm:grid-cols-4">
			{shareActions
				.filter(({ channel }) => CHANNELS.has(channel))
				.map(({ channel, ShareAction }) => {
					const ch = channel as Channel;
					const IconComponent = ICON_MAP[ch];
					const label = getLabel(ch, translate);
					return (
						<ShareAction key={channel}>
							<SocialSharing
								icon={<IconComponent />}
								label={label}
								size="large"
								variant={VARIANT_MAP[ch]}
								className="mt-6 justify-self-center"
								aria-label={label}
								data-testid={`movement-share-${channel}`}
								data-qa={`movement-share-${channel}`}
							/>
						</ShareAction>
					);
				})}
		</SocialSharingGroup>
	);
};
